import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { ShortModel } from '@data-layer/models/common';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarineInsuranceAvailabilityService, MarineInsuranceHelperService } from '../../services';
import { enumInsuranceShipmentType, enumMarineInsuranceCommodity, enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceQuoteFormModel } from '@shared/marine-insurance-shared/models';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IsfModel } from '@data-layer/models/isf';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { MarineInsuranceManagementModel, MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { enumIsfState } from '@data-layer/enums/isf';
import { enumEntrySummaryState, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { marineInsuranceAmountMaxLimits } from '../../constants/marine-insurance.const';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { DateTimezoneService } from '@services/date-timezone.service';
import { DateHelper } from '@helpers/date.helper';

@Component({
  selector: 'app-marine-insurance-sidebar-management-form',
  templateUrl: './marine-insurance-sidebar-quote-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-quote-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarQuoteFormComponent implements OnInit, OnDestroy {
  @Input() public entity: enumMarineInsuranceEntityType;
  @Input() public marineInsurance: MarineInsuranceModel | MarineInsuranceManagementModel;
  @Input() public isf: IsfModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public lclQuote: QuoteModel;
  @Input() public lclShipment: LCLShipmentModel;
  @Input() public isSaving: boolean;
  @Input() public isEdit: boolean;
  @Input() public commodities$: Observable<ShortModel[]>;

  @Output() public save: EventEmitter<{
    data: MarineInsuranceQuoteFormModel;
    entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel;
  }> = new EventEmitter<{
    data: MarineInsuranceQuoteFormModel;
    entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel;
  }>();

  public formGroup: UntypedFormGroup;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public enumInsuranceShipmentType: typeof enumInsuranceShipmentType = enumInsuranceShipmentType;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public errors: { [name: string]: IErrorForm } = {
    insuredAmount: {
      required: 'FORM_ERROR.REQUIRED',
      min: 'MARINE_INSURANCE_LIMITED_UP_FROM',
      max: 'MARINE_INSURANCE_LIMITED_UP_TO',
    },
    manufactureDate: {
      required: 'FORM_ERROR.REQUIRED',
    },
  };

  public cifAdded: boolean;
  public cifPercentage: number = 10;
  public isOpenedDescription: boolean;
  public maxManufaturerDate: string = this.dateTimeZoneService.endOfTheDay(DateHelper.now);

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private dateTimeZoneService: DateTimezoneService,
    private marineInsuranceHelperService: MarineInsuranceHelperService,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
    private cdr: ChangeDetectorRef,
  ) {}

  public detectChanges(): void {
    this.cdr.markForCheck();
  }

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      shippingDate: new UntypedFormControl(null),
      manufactureDate: new UntypedFormControl(null),
      insuredAmount: new UntypedFormControl(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(marineInsuranceAmountMaxLimits[enumMarineInsuranceCommodity.GENERAL]),
      ]),
      commodityId: new UntypedFormControl(enumMarineInsuranceCommodity.GENERAL, Validators.required),
      typeOfShipment: new UntypedFormControl(enumInsuranceShipmentType.COLOADING),
      goodsTypeDescription: new UntypedFormControl(null),
    });
    if (![enumMarineInsuranceEntityType.LCL_QUOTE, enumMarineInsuranceEntityType.LCL_SHIPPING].includes(this.entity)) {
      this.formGroup.get('shippingDate').setValidators(Validators.required);
      this.formGroup.get('shippingDate').updateValueAndValidity();
    }
    this.setTypeOfShipment(enumMarineInsuranceCommodity.GENERAL);

    this.formGroup
      .get('commodityId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: enumMarineInsuranceCommodity) => {
        this.setTypeOfShipment(val);

        this.formGroup
          .get('insuredAmount')
          .setValidators([Validators.required, Validators.min(1), Validators.max(this.insuredAmountLimit)]);
        this.formGroup.get('insuredAmount').updateValueAndValidity();
        this.formGroup.get('manufactureDate').updateValueAndValidity();

        this.isOpenedDescription = false;
      });

    if (![enumMarineInsuranceEntityType.LCL_QUOTE, enumMarineInsuranceEntityType.LCL_SHIPPING].includes(this.entity)) {
      this.formGroup
        .get('shippingDate')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.formGroup
            .get('shippingDate')
            .setErrors(this.dateErrorAir || this.dateErrorOcean ? { required: true } : null, { emitEvent: false });
        });
    }

    if (!!this.marineInsurance) {
      this.formGroup.patchValue({
        shippingDate: this.marineInsurance?.shippingDate,
        manufactureDate: this.manufactureDate,
        insuredAmount: this.marineInsurance?.insuredValue?.insuredAmount,
        commodityId: this.marineInsurance?.insuredValue?.commodity?.id,
        typeOfShipment: this.marineInsurance?.insuredValue?.typeOfShipment,
        goodsTypeDescription: this.marineInsurance?.insuredValue?.goodsTypeDescription,
      });
    } else {
      if (this.entity === enumMarineInsuranceEntityType.ENTRY_SUMMARY && this.entrySummary?.shipments?.[0]?.exportDate) {
        this.formGroup.get('shippingDate').setValue(this.entrySummary?.shipments?.[0]?.exportDate);
      }
      if ([enumMarineInsuranceEntityType.LCL_QUOTE, enumMarineInsuranceEntityType.LCL_SHIPPING].includes(this.entity)) {
        this.formGroup
          .get('shippingDate')
          .setValue(!!this.lclQuote ? this.lclQuote.voyage?.estimatedDepartureTime : this.lclShipment.voyage?.estimatedDepartureTime);
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public addCif(): void {
    if (!this.cifAdded && Number(this.formGroup.get('insuredAmount').value) > 0) {
      this.formGroup.get('insuredAmount').setValue(this.formGroup.get('insuredAmount').value * (1 + this.cifPercentage / 100));
      this.cifAdded = true;
    }
  }

  public setOpenedDescription(): void {
    this.isOpenedDescription = !this.isOpenedDescription;
  }

  public getCommodityDescription(commodityIdId: number): string {
    return this.marineInsuranceHelperService.getCommodityDescription(commodityIdId);
  }

  public isShowMore(commodityId: number): boolean {
    return [enumMarineInsuranceCommodity.GENERAL, enumMarineInsuranceCommodity.FRAGILES].includes(commodityId);
  }

  public isShowTransportationMode(commodityId: number): boolean {
    return commodityId === enumMarineInsuranceCommodity.VEHICLES;
  }

  public get isShowDepartureDate(): boolean {
    switch (this.entity) {
      case enumMarineInsuranceEntityType.ISF:
        return true;
      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        return !this.entrySummary?.shipments?.[0]?.exportDate;
      case enumMarineInsuranceEntityType.LCL_QUOTE:
      case enumMarineInsuranceEntityType.LCL_SHIPPING:
        return false;
      default:
        return true;
    }
  }

  public onSave(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.valid) {
      let entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel = null;
      let referenceNumber: string;
      switch (this.entity) {
        case enumMarineInsuranceEntityType.ISF:
          entity = this.isf;
          referenceNumber = this.isf?.referenceNumber;
          break;
        case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
          entity = this.entrySummary;
          referenceNumber = this.entrySummary?.referenceNumber;
          break;
        case enumMarineInsuranceEntityType.LCL_QUOTE:
          entity = this.lclQuote;
          referenceNumber = this.lclQuote?.referenceNumber;
          break;
        case enumMarineInsuranceEntityType.LCL_SHIPPING:
          entity = this.lclShipment;
          referenceNumber = this.lclShipment?.referenceNumber;
          break;
      }

      this.isSaving = true;

      const data = { ...this.formGroup.getRawValue() };
      const commodityId = this.formGroup.get('commodityId').value;
      if (this.isShowTransportationMode(commodityId)) {
        const manufactureDateControl = this.formGroup.get('manufactureDate');
        const manufactureDateYear = manufactureDateControl.value.toString();
        const manufactureDate = new Date(manufactureDateYear).getFullYear().toString();

        data.manufactureDate = manufactureDate;
      }

      this.save.emit({ data, entity });
    }
  }

  public get dateErrorAir(): boolean {
    return (
      this.entity === enumMarineInsuranceEntityType.ENTRY_SUMMARY &&
      this.entrySummary.transportationMode === enumTransportationMode.AIR &&
      !!this.formGroup.get('shippingDate').value &&
      !this.marineInsuranceAvailabilityService.validateDates(enumTransportationMode.AIR, this.formGroup.get('shippingDate').value)
    );
  }

  public get dateErrorOcean(): boolean {
    return (
      ((this.entity === enumMarineInsuranceEntityType.ENTRY_SUMMARY &&
        this.entrySummary.transportationMode === enumTransportationMode.OCEAN) ||
        this.entity === enumMarineInsuranceEntityType.ISF ||
        this.entity === enumMarineInsuranceEntityType.LCL_QUOTE ||
        this.entity === enumMarineInsuranceEntityType.LCL_SHIPPING) &&
      !!this.formGroup.get('shippingDate').value &&
      !this.marineInsuranceAvailabilityService.validateDates(enumTransportationMode.OCEAN, this.formGroup.get('shippingDate').value)
    );
  }

  public get isShowAfterCompletion(): boolean {
    switch (this.entity) {
      case enumMarineInsuranceEntityType.ISF:
        return this.isf?.state === enumIsfState.NOT_SENT;
      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        return this.entrySummary?.state === enumEntrySummaryState.NOT_SENT;
      default:
        return false;
    }
  }

  public get insuredAmountLimit(): number {
    return (
      marineInsuranceAmountMaxLimits[this.formGroup.get('commodityId').value] ||
      marineInsuranceAmountMaxLimits[enumMarineInsuranceCommodity.GENERAL]
    );
  }

  public get manufactureDate(): string {
    //Conversion of year string into full date string with year assuming we convert date to year only string upon saving
    const originalManufactureDate = this.marineInsurance?.updatedManufactureDate || this.marineInsurance?.manufactureDate;

    if (!originalManufactureDate) {
      return;
    }

    const newManufactureDate = this.dateTimeZoneService.formatInUserTZ(`${originalManufactureDate}-02-02`);
    return newManufactureDate;
  }

  private setTypeOfShipment(val: enumMarineInsuranceCommodity): void {
    this.formGroup.get('typeOfShipment').setValue(enumInsuranceShipmentType.COLOADING, { emitEvent: false });

    if (val === enumMarineInsuranceCommodity.VEHICLES) {
      this.formGroup.get('typeOfShipment').setValue(enumInsuranceShipmentType.BREAKBULK);
      this.formGroup.get('typeOfShipment').setValidators(Validators.required);
      this.formGroup.get('manufactureDate').setValidators(Validators.required);
    } else {
      this.formGroup.get('typeOfShipment').setValidators(null);
      this.formGroup.get('manufactureDate').setValidators(null);
    }
  }
}
